import type { DateTimeFiltersProps } from './DateTimeFilters';

import { Box, Collapsible, InlineStack, Text, Icon } from '@shopify/polaris';
import { ChevronUpIcon, ChevronDownIcon } from '@shopify/polaris-icons';
import { GDiv, GOptionList } from '~/presentation/ui';
import { useToggle } from '~/hooks/useToggle';
import { dayjsTz } from '~/utils/dayjs';
import { cls } from '~/utils';

export function DateTimeFilterAddition(props: DateTimeFiltersProps) {
  const { setDateTimePicked, dateTimePicked, rangeAddition, allRanges } = props;
  const { value: isExpanded, toggle: toggleExpanded } = useToggle(true);

  const rangesAdditions = rangeAddition?.rangers || [];

  const handleChangeFilterByOption = (value: string[]) => {
    const alias = value[0];
    const result = allRanges.find((range) => range.alias === alias) || allRanges[0];

    setDateTimePicked({
      start: dayjsTz(result.since).toDate(),
      end: dayjsTz(result.until).toDate(),
      alias: result.alias,
    });
  };

  const options = rangesAdditions.map((range) => {
    const selected = dateTimePicked.alias === range.alias;
    return {
      value: range.alias,
      label: (
        <Text as="span" truncate>
          <Text as="span" variant={'bodyMd'} fontWeight={selected ? 'semibold' : 'regular'}>
            {range.title}
          </Text>
          {range.description && (
            <>
              {' '}
              <Text as="span" variant={'bodyXs'} fontWeight={'regular'} tone="subdued">
                {`(${range.description})`}
              </Text>
            </>
          )}
        </Text>
      ),
    };
  });

  if (!rangeAddition || rangesAdditions.length === 0) return;

  return (
    <Box borderBlockEndWidth={'025'} borderColor={'border'} paddingBlock={'200'}>
      <div className="px-1.5">
        <GDiv
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          className={cls('p-1.5 rounded-lg cursor-pointer hover:bg-surface-hover')}
        >
          <InlineStack align="space-between" blockAlign="center">
            <Text as="span" variant="bodyMd">
              {rangeAddition.title}
            </Text>
            <Box>
              <Icon source={isExpanded ? ChevronUpIcon : ChevronDownIcon} />
            </Box>
          </InlineStack>
        </GDiv>
      </div>

      <Collapsible
        open={isExpanded}
        id="date-time-filter-periods-collapsible"
        transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
      >
        <GOptionList
          options={options}
          selected={dateTimePicked ? [dateTimePicked.alias] : []}
          onChange={handleChangeFilterByOption}
        />
      </Collapsible>
    </Box>
  );
}
